import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmQuestion } from 'components/v2/confirm-dialog/confirmQuestion';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { toaster } from 'components/v2/toast';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useRef, useState } from 'react';
import {
  CompanyMigrationLogModel,
  CompanyMigrationOperation,
  CompanyMigrationRunModel,
  CompanyMigrationSource,
  CompanyMigrationState,
  deleteAdminCompanyMigrationRun,
  getAdminCompanyMigrationLog,
  getAdminCompanyMigrations,
  requestAdminCompanyMigration,
} from 'services/api/adminCompanyMigrationApi';
import { QUERY_KEYS } from 'services/api/constants';
import styled from 'styled-components';
import { FormPanel } from 'styles/v2/Styles.styled';
import { getApiErrorDetail } from 'util/apiErrorUtils';
import CompanyMigrationRunResults, { Badge, Tone, rowNumberColumn, toneColors } from './CompanyMigrationRunResults';

/** The formats a company can be migrated from. Compiler is added here once its migration exists. */
const SOURCE_LABELS: Record<CompanyMigrationSource, string> = {
  SimpleWizard: 'Simple Wizard',
};

const SOURCE_OPTIONS = (Object.keys(SOURCE_LABELS) as CompanyMigrationSource[]).map((value) => ({
  value,
  label: SOURCE_LABELS[value],
}));

const OPERATION_LABELS: Record<CompanyMigrationOperation, string> = {
  DryRun: 'Dry run',
  Rehearsal: 'Rehearsal',
  Rollback: 'Roll back',
};

const OPERATION_ICONS: Record<CompanyMigrationOperation, string> = {
  DryRun: 'pi pi-search',
  Rehearsal: 'pi pi-copy',
  Rollback: 'pi pi-undo',
};

const OPERATIONS: CompanyMigrationOperation[] = ['DryRun', 'Rehearsal', 'Rollback'];

const describeOperation = (operation: CompanyMigrationOperation, source: CompanyMigrationSource) => {
  const sourceLabel = SOURCE_LABELS[source];
  switch (operation) {
    case 'DryRun':
      return {
        caption: 'Start a dry run?',
        description: `Every ${sourceLabel} EPD of the company is translated, saved and validated inside a transaction that is rolled back. Nothing changes for users; only the results are kept.`,
      };
    case 'Rehearsal':
      return {
        caption: 'Start a rehearsal?',
        description: `The company is locked while Hidden Dynamic Forms copies of its ${sourceLabel} EPDs are inserted, then unlocked. Users see no change. The copies can only be rolled back, never switched on.`,
      };
    default:
      return {
        caption: 'Roll back the Dynamic Forms copies?',
        description:
          'Every Dynamic Forms copy of the company is deleted. The company is locked during the rollback and then restored to its lock state before the rehearsal.',
      };
  }
};

const POLL_INTERVAL_MS = 5000;

const STATE_LABELS: Record<CompanyMigrationState, string> = {
  Pending: 'Queued',
  InProgress: 'Running',
  Succeeded: 'Completed',
  Failed: 'Failed',
};

const STATE_TONES: Record<CompanyMigrationState, Tone> = {
  Pending: 'gray',
  InProgress: 'blue',
  Succeeded: 'green',
  Failed: 'red',
};

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '');

const hasResults = (run: CompanyMigrationRunModel) =>
  run.operation !== 'Rollback' && (run.state === 'Succeeded' || run.state === 'Failed');

/**
 * One line per run: what happened to the company's EPDs. Blocked EPDs are the only ones that could not be migrated;
 * follow-ups and notes are reported but are not failures.
 */
const describeOutcome = (run: CompanyMigrationRunModel) => {
  if (run.state === 'Pending' || run.state === 'InProgress') {
    return '';
  }

  if (run.operation === 'Rollback') {
    return `${run.migratedCount} cop${run.migratedCount === 1 ? 'y' : 'ies'} removed`;
  }

  return [
    `${run.migratedCount} migrated`,
    `${run.blockedCount} blocked`,
    `${run.followUpEpdCount} with owner follow-ups`,
    `${run.notedEpdCount} with mapping notes`,
  ].join(' · ');
};

/**
 * Migration runs of a company into Dynamic Forms (System Administrator only): pick the source format, request a dry
 * run, a rehearsal or a rollback, follow the runs, click a run to see its EPDs (and an EPD to see its issues), open a
 * run's technical log, and delete a finished dry run. Which actions are allowed comes from the server; the list
 * refreshes while a run is queued or running.
 */
const CompanyMigrationRuns: React.FunctionComponent<{ companyId: string; onRunFinished: () => void }> = ({
  companyId,
  onRunFinished,
}) => {
  const queryClient = useQueryClient();
  const [source, setSource] = useState<CompanyMigrationSource>('SimpleWizard');
  const [logRun, setLogRun] = useState<CompanyMigrationRunModel | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const queryKey = [QUERY_KEYS.ADMIN_COMPANY_MIGRATIONS, companyId];

  const runsQuery = useQuery({
    queryKey,
    queryFn: () => getAdminCompanyMigrations(companyId),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.isBusy ? POLL_INTERVAL_MS : false),
  });

  // A finished rehearsal or rollback changes the copies and the lock shown above the panel.
  const wasBusy = useRef(false);
  const isBusy = runsQuery.data?.isBusy ?? false;
  useEffect(() => {
    if (wasBusy.current && !isBusy) {
      onRunFinished();
    }
    wasBusy.current = isBusy;
  }, [isBusy, onRunFinished]);

  const requestMutation = useMutation({
    mutationFn: (operation: CompanyMigrationOperation) => requestAdminCompanyMigration(companyId, operation),
    onSuccess: (_, operation) =>
      toaster({
        severity: 'success',
        summary: 'Success',
        details: `${OPERATION_LABELS[operation]} queued. It starts within a minute.`,
      }),
    onError: (error) =>
      toaster({ severity: 'error', summary: 'Error', details: getApiErrorDetail(error) ?? 'Failed to queue the run.' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const deleteMutation = useMutation({
    mutationFn: (run: CompanyMigrationRunModel) => deleteAdminCompanyMigrationRun(companyId, run.id),
    onSuccess: (_, run) => {
      if (run.id === selectedRunId) {
        setSelectedRunId(null);
      }

      toaster({ severity: 'success', summary: 'Success', details: 'The dry run has been deleted.' });
    },
    onError: (error) =>
      toaster({ severity: 'error', summary: 'Error', details: getApiErrorDetail(error) ?? 'Failed to delete the dry run.' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const confirmDelete = (run: CompanyMigrationRunModel) =>
    confirmQuestion({
      caption: 'Delete the dry run?',
      description: `The dry run of ${formatDate(run.requestedAt)} and its results are deleted. Nothing else changes.`,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () => deleteMutation.mutate(run),
    });

  const confirmRequest = (operation: CompanyMigrationOperation) =>
    confirmQuestion({
      ...describeOperation(operation, source),
      acceptLabel: OPERATION_LABELS[operation],
      rejectLabel: 'Cancel',
      onConfirm: () => requestMutation.mutate(operation),
    });

  const model = runsQuery.data;
  const isDisabled = requestMutation.isPending || !model;
  const isAllowed: Record<CompanyMigrationOperation, boolean> = {
    DryRun: !!model?.canDryRun,
    Rehearsal: !!model?.canRehearse,
    Rollback: !!model?.canRollBack,
  };

  // The results shown: the run the user picked, otherwise the latest finished dry run or rehearsal.
  const runs = model?.runs ?? [];
  const resultsRun = runs.find((run) => run.id === selectedRunId) ?? runs.find(hasResults) ?? null;

  // The row buttons must not also select the row.
  const actionsBodyTemplate = (run: CompanyMigrationRunModel) => (
    <RowButtons>
      <Button
        label="Log"
        className="p-button-sm p-button-text"
        onClick={(e) => {
          e.stopPropagation();
          setLogRun(run);
        }}
      />
      {run.canDelete && (
        <Button
          icon="pi pi-trash"
          className="p-button-sm p-button-text p-button-danger"
          aria-label="Delete the dry run"
          tooltip="Delete the dry run"
          disabled={deleteMutation.isPending}
          onClick={(e) => {
            e.stopPropagation();
            confirmDelete(run);
          }}
        />
      )}
    </RowButtons>
  );

  return (
    <Panel>
      <PanelHeader>
        <div>
          <h3>Migration to Dynamic Forms</h3>
          <Hint>
            Try the migration of the company's {SOURCE_LABELS[source]} EPDs without affecting users: a dry run reports what
            would migrate, a rehearsal inserts Hidden copies, a rollback removes them.
          </Hint>
        </div>
      </PanelHeader>

      <Toolbar>
        <SourceField>
          <label htmlFor="migrationSource">Migrate from</label>
          <Dropdown
            inputId="migrationSource"
            value={source}
            options={SOURCE_OPTIONS}
            optionLabel="label"
            optionValue="value"
            disabled={isBusy}
            onChange={(e) => setSource(e.value as CompanyMigrationSource)}
          />
        </SourceField>
        <RowButtons>
          {OPERATIONS.map((operation) => (
            <Button
              key={operation}
              label={OPERATION_LABELS[operation]}
              icon={OPERATION_ICONS[operation]}
              className={`p-button-sm${operation === 'Rollback' ? ' p-button-danger p-button-outlined' : ''}`}
              disabled={isDisabled || !isAllowed[operation]}
              tooltip={describeOperation(operation, source).description}
              tooltipOptions={{ position: 'top', showOnDisabled: true, style: { maxWidth: '24rem' } }}
              onClick={() => confirmRequest(operation)}
            />
          ))}
        </RowButtons>
      </Toolbar>

      {runsQuery.isError && (
        <ErrorText>{getApiErrorDetail(runsQuery.error) ?? 'Failed to load the migration runs.'}</ErrorText>
      )}
      {model && !model.isEnabled && (
        <Notice $tone="gray">
          <i className="pi pi-ban" />
          The company migration is switched off in this environment.
        </Notice>
      )}
      {isBusy && (
        <Notice $tone="blue">
          <i className="pi pi-spin pi-spinner" />A run is queued or running. The list refreshes every{' '}
          {POLL_INTERVAL_MS / 1000} seconds.
        </Notice>
      )}
      {model?.hasCopies && (
        <Notice $tone="yellow">
          <i className="pi pi-exclamation-triangle" />
          The company has Dynamic Forms copies from a rehearsal. Roll them back before the next run.
        </Notice>
      )}

      {runs.some(hasResults) && <Hint>Click a dry run or rehearsal to see its EPDs below.</Hint>}
      <DataTableStyled
        value={runs}
        emptyMessage="No migration runs yet."
        dataKey="id"
        selectionMode="single"
        selection={resultsRun}
        isDataSelectable={(e) => hasResults(e.data as CompanyMigrationRunModel)}
        onSelectionChange={(e) => {
          const run = e.value as CompanyMigrationRunModel | null;
          if (run && hasResults(run)) {
            setSelectedRunId(run.id);
          }
        }}
        rowClassName={(run: CompanyMigrationRunModel) => (hasResults(run) ? 'migration-selectable-row' : '')}
      >
        {rowNumberColumn()}
        <Column
          field="operation"
          header="Operation"
          body={(run: CompanyMigrationRunModel) => OPERATION_LABELS[run.operation] ?? run.operation}
        />
        <Column
          field="state"
          header="State"
          body={(run: CompanyMigrationRunModel) => (
            <Badge $tone={STATE_TONES[run.state] ?? 'gray'}>{STATE_LABELS[run.state] ?? run.state}</Badge>
          )}
        />
        <Column
          header="Result"
          body={(run: CompanyMigrationRunModel) => describeOutcome(run)}
          style={{ minWidth: '20rem' }}
        />
        <Column field="requestedBy" header="Requested by" />
        <Column
          field="requestedAt"
          header="Requested at"
          body={(run: CompanyMigrationRunModel) => formatDate(run.requestedAt)}
        />
        <Column
          field="completedAt"
          header="Completed at"
          body={(run: CompanyMigrationRunModel) => formatDate(run.completedAt)}
        />
        <Column
          field="failureReason"
          header="Run failure"
          body={(run: CompanyMigrationRunModel) =>
            run.failureReason ? <ErrorText as="span">{run.failureReason}</ErrorText> : null
          }
          style={{ minWidth: '12rem' }}
        />
        <Column header="" body={actionsBodyTemplate} style={{ minWidth: '11rem' }} />
      </DataTableStyled>

      {resultsRun && <CompanyMigrationRunResults key={resultsRun.id} companyId={companyId} run={resultsRun} />}

      {logRun && <RunLogDialog companyId={companyId} run={logRun} onHide={() => setLogRun(null)} />}
    </Panel>
  );
};

/** The technical log of a run: every step, for troubleshooting. */
const RunLogDialog: React.FunctionComponent<{ companyId: string; run: CompanyMigrationRunModel; onHide: () => void }> = ({
  companyId,
  run,
  onHide,
}) => {
  const logQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_COMPANY_MIGRATION_LOG, companyId, run.id],
    queryFn: () => getAdminCompanyMigrationLog(companyId, run.id),
    refetchOnWindowFocus: false,
  });

  return (
    <Dialog
      header={`${OPERATION_LABELS[run.operation]} ${formatDate(run.requestedAt)} - technical log`}
      visible
      onHide={onHide}
      style={{ width: '90vw' }}
    >
      {logQuery.error && <ErrorText>{getApiErrorDetail(logQuery.error) ?? 'Failed to load.'}</ErrorText>}
      <DataTableStyled value={logQuery.data ?? []} loading={logQuery.isLoading} emptyMessage="No log rows.">
        {rowNumberColumn()}
        <Column field="createdAt" header="Time" body={(row: CompanyMigrationLogModel) => formatDate(row.createdAt)} />
        <Column field="step" header="Step" />
        <Column field="level" header="Level" />
        <Column field="registrationNumber" header="Reg. no." />
        <Column field="versionNumber" header="Version" />
        <Column field="message" header="Message" style={{ minWidth: '24rem' }} />
        <Column
          field="detailsJson"
          header="Details"
          body={(row: CompanyMigrationLogModel) => (row.detailsJson ? <Json>{row.detailsJson}</Json> : null)}
        />
      </DataTableStyled>
    </Dialog>
  );
};

const Panel = styled(FormPanel)`
  display: block;
  padding: 1rem;
  margin-top: 1rem;

  .migration-selectable-row {
    cursor: pointer;
  }
`;

const Hint = styled.div`
  padding: 0.25rem 0 0.5rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.statusLabelGrayText};
`;

const PanelHeader = styled.div`
  h3 {
    margin: 0 0 0.25rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  padding: 0.75rem 0;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.statusLabelGrayBg};
`;

const SourceField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 14rem;

  label {
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

const RowButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Notice = styled.div<{ $tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  font-weight: 600;
  color: ${(props) => toneColors(props.theme, props.$tone).text};
  background: ${(props) => toneColors(props.theme, props.$tone).bg};
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.statusLabelRedText};
  padding: 0.5rem 0;
`;

const Json = styled.pre`
  max-width: 40rem;
  max-height: 12rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 0.75rem;
  margin: 0;
`;

export default CompanyMigrationRuns;
